import { externalLaunchContextAtom } from '@/atoms/externalLaunchAuthAtoms';
import { resolveOnboardingProvider } from '@/hooks/integrations/resolveIntegrationProvider';
import { useOldFrameworkIntegrations } from '@/hooks/integrations/useOldFrameworkIntegrations';
import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import {
  isEhrIntegrationProvider,
  type SearchIntegrationOnboardingSessionsResponse,
} from '@heidi/integrations/connected-apps/contracts';
import { type EHRProvider, OnboardingSessionStatus } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

const selectIntegrationOnboardingSession = ({
  data,
  genericOnboardingSessionId,
  ehrProviderFromSource,
}: {
  data: SearchIntegrationOnboardingSessionsResponse;
  genericOnboardingSessionId?: string | null;
  ehrProviderFromSource?: EHRProvider | null;
}) => {
  // Honor purpose metadata and keep known non-EHR providers out when an older
  // backend has not yet supplied that additive field.
  const ehrSessions = data.sessions.filter(
    (session) =>
      session.provider_purpose !== 'connected_app' &&
      isEhrIntegrationProvider(session.provider)
  );
  const selectedSessionByGenericOnboardingSessionId = genericOnboardingSessionId
    ? ehrSessions.find((session) => session.id === genericOnboardingSessionId)
    : undefined;
  const resolvedEhrProviderFromSource = ehrProviderFromSource
    ? resolveOnboardingProvider(ehrProviderFromSource)
    : undefined;
  const selectedSessionByEhrProvider = resolvedEhrProviderFromSource
    ? ehrSessions.find(
        (session) => (session.provider as EHRProvider) === resolvedEhrProviderFromSource
      )
    : undefined;

  return {
    sessions: ehrSessions,
    selectedSession:
      selectedSessionByGenericOnboardingSessionId ??
      selectedSessionByEhrProvider ??
      // Account-default fallback only when the caller gave no provider hint; a
      // hinted surface that matches no onboarding session (e.g. an Epic FHIR
      // launch) must not inherit another provider's integration context.
      (resolvedEhrProviderFromSource
        ? undefined
        : ehrSessions.find((session) => isEhrIntegrationProvider(session.provider))),
  };
};

export const useIntegrationOnboardingSessionResolver = (
  ehrProviderFromSource?: EHRProvider | null
) => {
  const externalLaunchContext = useAtomValue(externalLaunchContextAtom);
  const oldFrameworkIntegrations = useOldFrameworkIntegrations();
  const selectOnboardingSessionFn = useCallback(
    (data: SearchIntegrationOnboardingSessionsResponse) =>
      selectIntegrationOnboardingSession({
        data,
        genericOnboardingSessionId: externalLaunchContext?.onboardingSessionId,
        ehrProviderFromSource,
      }),
    [ehrProviderFromSource, externalLaunchContext?.onboardingSessionId]
  );

  const {
    data: integrationOnboardingSessionData,
    isPending: isOnboardingSessionPending,
    isError: isOnboardingSessionError,
    fetchStatus: isOnboardingSessionFetchStatus,
    dataUpdatedAt: onboardingSessionDataUpdatedAt,
  } = useQueryIntegrationOnboardingSessions({
    params: {
      status: OnboardingSessionStatus.completed,
    },
    select: selectOnboardingSessionFn,
  });

  const {
    sessions: integrationOnboardingSessions,
    selectedSession: integrationOnboardingSession,
  } = integrationOnboardingSessionData ?? {};
  const selectedOnboardingProvider = integrationOnboardingSession?.provider as
    | EHRProvider
    | undefined;

  const isIntegrationOnboardingSession = useMemo(() => {
    return Boolean(
      selectedOnboardingProvider &&
      !oldFrameworkIntegrations.includes(selectedOnboardingProvider)
    );
  }, [selectedOnboardingProvider, oldFrameworkIntegrations]);

  return {
    integrationOnboardingSessions,
    integrationOnboardingSession,
    selectedOnboardingProvider,
    isOnboardingSessionPending,
    isOnboardingSessionError,
    // Same shape as `useLegacyIntegrationSource`: "actually resolving" is
    // `isPending && fetchStatus === 'fetching'` (disabled stays
    // idle-forever, and a paused fetch is `paused`, not `fetching`, so an
    // offline cold start does not hold the loading canvas forever;
    // `isPending` self-limits to the first settle); and only an initial load
    // that never succeeded (`dataUpdatedAt === 0`) is a loading error,
    // because the post-`select` data is `undefined` for users with no
    // matching onboarding session, so TanStack's own `isLoadingError` would
    // misread any refetch error as one.
    isOnboardingSessionLoading:
      isOnboardingSessionPending && isOnboardingSessionFetchStatus === 'fetching',
    isOnboardingSessionLoadingError:
      isOnboardingSessionError && onboardingSessionDataUpdatedAt === 0,
    isIntegrationOnboardingSession,
  };
};
